/** Theme handling (spec §10.1). The choice lives in user_preference('app'); localStorage only mirrors it
 * so public/theme-init.js can apply it before first paint. */

export type ThemeChoice = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'crm.theme'
const CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark']
// Matches --background in index.css for each theme (zinc-50 light, zinc-950 dark).
const THEME_COLOURS: Record<ResolvedTheme, string> = { light: '#fafafa', dark: '#09090b' }

export function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(choice: ThemeChoice, systemDark: boolean = prefersDark()): ResolvedTheme {
  if (choice === 'system') return systemDark ? 'dark' : 'light'
  return choice
}

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && (CHOICES as readonly string[]).includes(value)
}

export function readThemeMirror(): ThemeChoice | null {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY)
    return isThemeChoice(value) ? value : null
  } catch {
    return null
  }
}

export function clearThemeMirror(): void {
  try {
    localStorage.removeItem(THEME_STORAGE_KEY)
  } catch {
    // Storage blocked: nothing to clear.
  }
}

export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const resolved = resolveTheme(choice)
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOURS[resolved])
  try {
    localStorage.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // Storage blocked: the theme still applies for this page.
  }
  return resolved
}
